/**
 * Documents d'un agrégateur — repris de la version du client.
 *
 * Deux écarts avec l'original, et un seul est visible ici : les champs portent
 * leurs noms français, comme partout dans ce dépôt. L'autre est invisible et plus
 * important : les octets vont sur GridFS et non vers le service objet hébergé par
 * Emergent, ce qui les fait entrer dans la sauvegarde quotidienne.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/shared/api/client';
import { messageErreurTelechargement } from '@/shared/utils/download';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { toast } from 'sonner';
import {
  Upload, FileText, Download, Trash2, FolderOpen, FileSpreadsheet,
  FileImage, File as FileIcon, Loader2,
} from 'lucide-react';

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
// Types acceptés : PDF, Word, Excel, images
const ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.gif,application/pdf,' +
  'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'image/*';

function fileIconFor(name = '', ct = '') {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['pdf'].includes(ext) || ct.includes('pdf')) return FileText;
  if (['xls', 'xlsx', 'csv'].includes(ext) || ct.includes('sheet')) return FileSpreadsheet;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(ext) || ct.startsWith('image/')) return FileImage;
  if (['doc', 'docx'].includes(ext) || ct.includes('word')) return FileText;
  return FileIcon;
}

function fmtSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AggregatorDocuments({ aggregator, colorClass = 'text-neutral-700', accentBg = 'bg-neutral-50' }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/aggregator-docs/${aggregator}`);
      setDocs(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger les documents');
    }
    setLoading(false);
  }, [aggregator]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.taille > MAX_SIZE) {
      toast.error(`Fichier trop volumineux (${fmtSize(file.taille)}). Max 10 Mo.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/aggregator-docs/${aggregator}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`« ${file.name} » importé`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    }
    setUploading(false);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/aggregator-docs/${aggregator}/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: doc.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nom_fichier || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(await messageErreurTelechargement(
        e, 'Téléchargement impossible. Le document n’a pas pu être récupéré.'));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.nom_fichier} » ? Cette action est définitive.`)) return;
    try {
      await api.delete(`/aggregator-docs/${aggregator}/${doc.id}`);
      toast.success('Document supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <Card className="border-neutral-200/80" data-testid={`aggregator-docs-${aggregator}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className={`text-sm font-semibold text-neutral-900 flex items-center gap-2`}>
              <FolderOpen className={`w-4 h-4 ${colorClass}`} />
              Gestion des documents
            </p>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Importez PDF, Word, Excel ou images (max 10 Mo). Historique, téléchargement, suppression.
            </p>
          </div>
          <Badge variant="outline" className={`text-[10px] ${accentBg}`} data-testid={`aggregator-docs-count-${aggregator}`}>
            {docs.length} document{docs.length > 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Zone d'upload */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragging ? 'border-emerald-400 bg-emerald-50/40' : 'border-neutral-200 bg-neutral-50/40 hover:bg-neutral-50'
          }`}
          data-testid={`aggregator-docs-dropzone-${aggregator}`}
        >
          <Upload className={`w-6 h-6 mx-auto mb-2 ${dragging ? 'text-emerald-500' : 'text-neutral-400'}`} />
          <p className="text-[12px] text-neutral-600 mb-2">
            Glissez-déposez un fichier ici ou
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            onChange={onFileChange}
            className="hidden"
            data-testid={`aggregator-docs-file-input-${aggregator}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`aggregator-docs-upload-btn-${aggregator}`}
          >
            {uploading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Envoi…</>
            ) : (
              <><Upload className="w-3.5 h-3.5 mr-1.5" />Sélectionner un fichier</>
            )}
          </Button>
          <p className="text-[10px] text-neutral-400 mt-2">
            Formats : PDF, DOC/DOCX, XLS/XLSX, PNG/JPG/WEBP · max 10 Mo
          </p>
        </div>

        {/* Historique */}
        {loading ? (
          <p className="text-[12px] text-neutral-400 text-center py-4">Chargement…</p>
        ) : docs.length === 0 ? (
          <p className="text-[12px] text-neutral-400 text-center py-4" data-testid={`aggregator-docs-empty-${aggregator}`}>
            Aucun document importé pour le moment.
          </p>
        ) : (
          <div className="border border-neutral-200/70 rounded-md overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-neutral-50 border-b border-neutral-200/70">
                  <tr className="text-left text-[10.5px] uppercase tracking-wide text-neutral-500">
                    <th className="py-2 px-3 font-medium">Fichier</th>
                    <th className="py-2 px-3 font-medium">Taille</th>
                    <th className="py-2 px-3 font-medium">Ajouté le</th>
                    <th className="py-2 px-3 font-medium">Par</th>
                    <th className="py-2 px-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => {
                    const Icon = fileIconFor(d.nom_fichier, d.content_type);
                    return (
                      <tr key={d.id} className="border-b border-neutral-100 hover:bg-neutral-50/40" data-testid={`aggregator-doc-row-${d.id}`}>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className={`w-4 h-4 flex-shrink-0 ${colorClass}`} />
                            <span className="truncate font-medium text-neutral-800">{d.nom_fichier}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-neutral-600 whitespace-nowrap">{fmtSize(d.taille)}</td>
                        <td className="py-2 px-3 text-neutral-600 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                        <td className="py-2 px-3 text-neutral-500 truncate max-w-[140px]">{d.depose_par_nom || d.depose_par_email || '—'}</td>
                        <td className="py-2 px-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => download(d)}
                              className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900"
                              title="Télécharger"
                              data-testid={`aggregator-doc-download-${d.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => remove(d)}
                              className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-600"
                              title="Supprimer"
                              data-testid={`aggregator-doc-delete-${d.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
